// src/services/community.ts
import {
  collection,
  query,
  orderBy,
  onSnapshot,
  doc,
  setDoc,
  updateDoc,
  serverTimestamp,
  collectionGroup,
  getDoc,
  limit,
  where,
  getDocs,
  writeBatch,
  increment,
  runTransaction,
  type Unsubscribe,
} from 'firebase/firestore';

import type { CommunityPost, Comment, CommunityMention } from '../types';
import { db, auth } from '../lib/firebase';
import { getUserByUsername } from './users';

import { Capacitor } from '@capacitor/core';
import { ensureJsAuthFromNative } from './authBridge';
import { completeDailyTaskAction } from './dailyRewards';


export type CommunityReactionKey = 'like' | 'love' | 'care' | 'laugh' | 'wow' | 'sad' | 'angry';

const COMMUNITY_REACTION_KEYS = new Set<CommunityReactionKey>([
  'like',
  'love',
  'care',
  'laugh',
  'wow',
  'sad',
  'angry',
]);

function normalizeCommunityReaction(value: any): CommunityReactionKey {
  const key = String(value || 'like').trim().toLowerCase() as CommunityReactionKey;
  return COMMUNITY_REACTION_KEYS.has(key) ? key : 'like';
}

function readLikeReaction(data: any): CommunityReactionKey {
  return normalizeCommunityReaction(data?.reaction || data?.type || data?.kind || 'like');
}

async function ensureAuthedForWrite(label: string) {
  try {
    const native = (() => {
      try {
        return Capacitor.isNativePlatform();
      } catch {
        return false;
      }
    })();

    if (!native) return;
    if (auth.currentUser?.uid) return;

    await Promise.race([
      ensureJsAuthFromNative(),
      new Promise((resolve) => setTimeout(resolve, 2500)),
    ]);
  } catch (e) {
    console.warn(`[community] ensureAuthedForWrite(${label}) failed`, e);
  }
}

const isNative =
  (() => {
    try {
      return (window as any)?.Capacitor?.isNativePlatform?.() === true;
    } catch {
      return false;
    }
  })();

const FIRESTORE_TIMEOUT_MS = isNative ? 30000 : 8000;

function withTimeout<T>(p: Promise<T>, label: string, ms = FIRESTORE_TIMEOUT_MS): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const t = setTimeout(
      () => reject({ code: 'timeout', message: `${label} timeout after ${ms}ms` }),
      ms,
    );
    p.then(
      (v) => {
        clearTimeout(t);
        resolve(v);
      },
      (e) => {
        clearTimeout(t);
        reject(e);
      },
    );
  });
}

/** Remove undefined keys so Firestore doesn’t reject payloads */
function stripUndefined<T extends Record<string, any>>(obj: T): Partial<T> {
  const safe: Record<string, any> = {};
  Object.keys(obj).forEach((k) => {
    const v = (obj as any)[k];
    if (v !== undefined) safe[k] = v;
  });
  return safe as Partial<T>;
}

function normalizeMillis(v: any): number {
  if (!v) return Date.now();
  if (typeof v === 'number') return v;
  if (typeof v?.toMillis === 'function') return v.toMillis();
  if (typeof v?.seconds === 'number') return v.seconds * 1000;
  return Date.now();
}


function isArchivedOrDeletedPost(post: any): boolean {
  const status = String(post?.status || '').toLowerCase();
  return post?.archived === true || Boolean(post?.deletedAt) || status === 'archived' || status === 'deleted' || status === 'hidden';
}

function isVisibleCommunityPost(post: any): boolean {
  return !isArchivedOrDeletedPost(post);
}

function normalizeCommunityPostFromSnap(docSnap: any): CommunityPost {
  const data = docSnap.data?.() ? (docSnap.data() as any) : ((docSnap as any) || {});
  const docId = String(docSnap.id || data.docId || data.id || '').trim();
  const embeddedId = data?.id ? String(data.id).trim() : '';

  // IMPORTANT: legacy community posts sometimes have data.id that does NOT match
  // the Firestore document id. All subcollection writes (comments/likes) must use
  // the actual document id, so id/docId are forced to docSnap.id and the old
  // embedded value is preserved only as legacyId.
  return {
    ...data,
    legacyId: embeddedId && embeddedId !== docId ? embeddedId : (data as any).legacyId,
    docId,
    id: docId,
    status: data.status || 'visible',
    archived: data.archived === true,
  } as CommunityPost;
}




function normalizeMentionHandle(value: string): string {
  return String(value || '').trim().replace(/^@+/, '').toLowerCase();
}

async function extractMentions(text: string): Promise<CommunityMention[]> {
  const source = String(text || '');
  const matches = [...source.matchAll(/(^|\s)@([a-zA-Z0-9_.-]{2,32})/g)];
  if (!matches.length) return [];

  const seen = new Set<string>();
  const mentions: CommunityMention[] = [];

  for (const match of matches) {
    const username = normalizeMentionHandle(match[2] || '');
    if (!username || seen.has(username)) continue;

    try {
      const user = await getUserByUsername(username);
      if (!user?.uid) continue;
      seen.add(username);
      mentions.push({
        uid: user.uid,
        username: user.username || username,
        displayName: user.displayName || user.username || username,
      });
    } catch (e) {
      console.warn('[community] extractMentions failed for', username, e);
    }
  }

  return mentions;
}

function normalizeCommentFromSnap(docSnap: any): Comment {
  const data = docSnap.data?.() ? docSnap.data() : (docSnap as any);
  return {
    id: docSnap.id ?? data.id,
    postId: data.postId,
    text: data.text ?? '',
    authorId: data.authorId,
    authorName: data.authorName ?? 'Angler',
    authorAvatar: data.authorAvatar ?? '',
    authorUsername: data.authorUsername ?? data.username ?? null,
    parentCommentId: data.parentCommentId ?? null,
    replyToAuthorId: data.replyToAuthorId ?? null,
    timestamp: normalizeMillis(data.timestamp ?? data.createdAt),
    createdAt: normalizeMillis(data.createdAt ?? data.timestamp),
    likeCount: typeof data.likeCount === 'number' ? data.likeCount : Array.isArray(data.likedBy) ? data.likedBy.length : 0,
    replyCount: typeof data.replyCount === 'number' ? data.replyCount : 0,
    likedBy: Array.isArray(data.likedBy) ? data.likedBy : [],
    status: data.status,
  } as any;
}

// ---------------------------------------------------------------------------
// Posts subscription
// ---------------------------------------------------------------------------

/**
 * Live subscription to community posts.
 * App.tsx expects a map keyed by post id.
 *
 * - PUBLIC: orderBy(timestamp desc), limit
 * - MINE (optional): where(authorId==uid) + orderBy(timestamp desc)
 * - If orderBy query fails due to index/orderBy/permission, fallback to limit-only and client-sort.
 * - One-time retry on the common “permission-denied right after login” race.
 */
export function subscribeCommunityPosts(
  onPosts: (posts: Record<string, CommunityPost>) => void,
  onError?: (err: any) => void,
  uid?: string,
): Unsubscribe {
  const postsCol = collection(db as any, 'communityPosts');

  let publicMap: Record<string, CommunityPost> = {};
  let mineMap: Record<string, CommunityPost> = {};

  const emitMerged = () => onPosts({ ...publicMap, ...mineMap });

  const emitFromSnap = (snap: any): Record<string, CommunityPost> => {
    const out: Record<string, CommunityPost> = {};
    snap.forEach((d: any) => {
      const post = normalizeCommunityPostFromSnap(d);
      if (post?.id && isVisibleCommunityPost(post)) out[post.id] = post;
    });
    return out;
  };

  const isPermissionRace = (err: any) => {
    const msg = String(err?.message || '');
    return err?.code === 'permission-denied' || msg.includes('permission') || msg.includes('insufficient');
  };

  const isMissingIndex = (err: any) => {
    const code = String(err?.code || '').toLowerCase();
    const msg = String(err?.message || '').toLowerCase();
    return (
      code === 'failed-precondition' &&
      (msg.includes('query requires an index') || msg.includes('requires an index'))
    );
  };

  let didRetry = false;
  let retryTimer: any = null;

  let unsubPublicPrimary: Unsubscribe | null = null;
  let unsubPublicFallback: Unsubscribe | null = null;

  let unsubMinePrimary: Unsubscribe | null = null;
  let unsubMineFallback: Unsubscribe | null = null;

  const stopAll = () => {
    try { unsubPublicPrimary?.(); } catch {}
    try { unsubPublicFallback?.(); } catch {}
    try { unsubMinePrimary?.(); } catch {}
    try { unsubMineFallback?.(); } catch {}
    unsubPublicPrimary = null;
    unsubPublicFallback = null;
    unsubMinePrimary = null;
    unsubMineFallback = null;
  };

  const startAll = () => {
    // -------------------------
    // PUBLIC FEED
    // -------------------------
    const publicPrimaryQ = query(
      postsCol,
      where('visibility', '==', 'public'),
      orderBy('timestamp', 'desc'),
      limit(250),
    );

    unsubPublicPrimary = onSnapshot(
      publicPrimaryQ,
      (snap) => {
        publicMap = emitFromSnap(snap);
        emitMerged();
      },
      (err) => {
        // 🔁 One-time retry on auth/token race immediately after login.
        if (uid && isPermissionRace(err) && !didRetry) {
          didRetry = true;
          try { stopAll(); } catch {}
          retryTimer = setTimeout(() => startAll(), 600);
          return;
        }

        // A missing composite index is not a feed failure. Fall back to the
        // rules-compatible public query and client-sort while Firestore builds it.
        // Other errors still bubble to App for auth/network recovery.
        if (!isMissingIndex(err)) onError?.(err);

        // Fallback: no orderBy, client-side sort
        const publicFallbackQ = query(postsCol, where('visibility', '==', 'public'), limit(250));
        unsubPublicFallback = onSnapshot(
          publicFallbackQ,
          (snap2) => {
            const rows: Array<{ id: string; data: any }> = [];
            snap2.forEach((d: any) => rows.push({ id: d.id, data: d.data() as any }));

            rows.sort((a, b) => {
              const ta = normalizeMillis(a.data?.timestamp ?? a.data?.createdAt);
              const tb = normalizeMillis(b.data?.timestamp ?? b.data?.createdAt);
              return tb - ta;
            });

            const out: Record<string, CommunityPost> = {};
            for (const r of rows) {
              const post = normalizeCommunityPostFromSnap({ id: r.id, data: () => r.data }) as any;
              if (isVisibleCommunityPost(post)) out[post.id] = post;
            }

            publicMap = out;
            emitMerged();
          },
          (err2) => onError?.(err2),
        );
      },
    );

    // -------------------------
    // MY POSTS (optional)
    // -------------------------
    if (uid) {
      const minePrimaryQ = query(
        postsCol,
        where('authorId', '==', uid),
        orderBy('timestamp', 'desc'),
        limit(250),
      );

      unsubMinePrimary = onSnapshot(
        minePrimaryQ,
        (snap) => {
          mineMap = emitFromSnap(snap);
          emitMerged();
        },
        (err) => {
          onError?.(err);

          // Fallback: where only, limit, client-sort
          const mineFallbackQ = query(postsCol, where('authorId', '==', uid), limit(250));

          unsubMineFallback = onSnapshot(
            mineFallbackQ,
            (snap2) => {
              const rows: Array<{ id: string; data: any }> = [];
              snap2.forEach((d: any) => rows.push({ id: d.id, data: d.data() as any }));

              rows.sort((a, b) => {
                const ta = normalizeMillis(a.data?.timestamp ?? a.data?.createdAt);
                const tb = normalizeMillis(b.data?.timestamp ?? b.data?.createdAt);
                return tb - ta;
              });

              const out: Record<string, CommunityPost> = {};
              for (const r of rows) {
                const post = normalizeCommunityPostFromSnap({ id: r.id, data: () => r.data }) as any;
                if (isVisibleCommunityPost(post)) out[post.id] = post;
              }

              mineMap = out;
              emitMerged();
            },
            (err2) => onError?.(err2),
          );
        },
      );
    } else {
      // no uid => no mine listener
      mineMap = {};
      emitMerged();
    }
  };

  startAll();

  return () => {
    try { if (retryTimer) clearTimeout(retryTimer); } catch {}
    stopAll();
  };
}

// ---------------------------------------------------------------------------
// Alias for App.tsx naming (canonical)
// ---------------------------------------------------------------------------
export function subscribeToCommunityPosts(
  onPosts: (posts: Record<string, CommunityPost>) => void,
  onError?: (err: any) => void,
  uid?: string,
): Unsubscribe {
  const resolvedUid = uid ?? auth.currentUser?.uid ?? undefined;
  return subscribeCommunityPosts(onPosts, onError, resolvedUid);
}

export function subscribeCommentsForPosts(
  postIds: string[],
  onCommentsByPost: (commentsByPost: Record<string, Comment[]>) => void,
  opts?: { limitPerPost?: number },
): () => void {
  const limitPerPost = opts?.limitPerPost ?? 50;

  const unsubs: Array<() => void> = [];
  const latestByPost: Record<string, Comment[]> = {};
  let isDead = false;

  const emit = () => {
    if (isDead) return;
    onCommentsByPost({ ...latestByPost });
  };

  const safeIds = Array.from(new Set((postIds || []).filter(Boolean))).slice(0, 50);

  for (const postId of safeIds) {
    const commentsRef = collection(db, 'communityPosts', postId, 'comments');
    const qRef = query(commentsRef, orderBy('timestamp', 'asc'), limit(limitPerPost));

    const unsub = onSnapshot(
      qRef,
      (snap) => {
        const items: Comment[] = [];
        snap.forEach((docSnap) => items.push(normalizeCommentFromSnap(docSnap)));
        latestByPost[postId] = items;
        emit();
      },
      (err) => {
        const code = String((err as any)?.code || '').toLowerCase();
        const message = String((err as any)?.message || '').toLowerCase();
        const permissionDenied =
          code === 'permission-denied' ||
          message.includes('missing or insufficient permissions') ||
          message.includes('insufficient permissions');

        // A post can disappear from the visible feed (archive/delete/moderation)
        // a moment before React tears down this per-post comments listener. The
        // Firestore rules intentionally deny child reads once the parent is no
        // longer visible, so treat that short listener race as an empty comment
        // state instead of surfacing a scary console error. Do not retry and do
        // not weaken rules.
        if (permissionDenied) {
          latestByPost[postId] = [];
          emit();
          return;
        }

        console.error('[LISTENER] community:commentsByPost', postId, err);
      },
    );

    unsubs.push(unsub);
  }

  return () => {
    isDead = true;
    unsubs.forEach((u) => {
      try { u(); } catch {}
    });
  };
}

// ---------------------------------------------------------------------------
// CRUD
// ---------------------------------------------------------------------------

// Same post id + concurrent retry = one write promise. This complements the UI lock
// and keeps retries idempotent because communityPosts/{post.id} is deterministic.
const communityPostCreateInFlight = new Map<string, Promise<void>>();

export async function createCommunityPost(post: CommunityPost): Promise<void> {
  const postId = String(post?.id || '').trim();
  if (!postId) throw new Error('Missing community post id');
  const existing = communityPostCreateInFlight.get(postId);
  if (existing) return existing;

  const task = (async () => {
  await ensureAuthedForWrite('createCommunityPost');

  const ref = doc(db as any, 'communityPosts', post.id);
  const text = (post as any).text ?? (post as any).caption ?? '';
  const mentions = await extractMentions(text);

  const data: any = stripUndefined({
    ...post,
    id: post.id,
    visibility: (post as any).visibility ?? 'public',
    status: (post as any).status ?? 'visible',
    archived: false,
    deletedAt: null,
    deletedBy: null,
    authorId: (post as any).authorId,
    authorName: (post as any).authorName,
    authorAvatar: (post as any).authorAvatar ?? '',
    caption: (post as any).caption ?? (post as any).text ?? '',
    text,
    mentions,
    timestamp: (post as any).timestamp ?? Date.now(),

    // initialize counters
    likeCount: Number((post as any).likeCount ?? 0),
    commentCount: Number((post as any).commentCount ?? 0),
    lastCommentAt: (post as any).lastCommentAt ?? null,

    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  console.log('[community] createCommunityPost:start', {
    postId: post?.id,
    authorId: (post as any)?.authorId,
    origin: typeof window !== 'undefined' ? window.location.origin : 'n/a',
  });

  await withTimeout(setDoc(ref, data), 'setDoc(communityPosts)');
  void completeDailyTaskAction('share_to_bay').catch((err) => {
    console.warn('[DailyTask] share_to_bay skipped', err);
  });
  console.log('[community] createCommunityPost:done', { postId: post?.id });
  })();

  communityPostCreateInFlight.set(postId, task);
  try {
    await task;
  } finally {
    if (communityPostCreateInFlight.get(postId) === task) {
      communityPostCreateInFlight.delete(postId);
    }
  }
}

export async function updateCommunityPost(postId: string, updated: Partial<CommunityPost>): Promise<void> {
  await ensureAuthedForWrite('updateCommunityPost');
  const ref = doc(db as any, 'communityPosts', postId);
  const nextText = (updated as any)?.text ?? (updated as any)?.caption;
  const mentions = typeof nextText === 'string' ? await extractMentions(nextText) : undefined;
  await updateDoc(ref, stripUndefined({ ...updated, ...(mentions ? { mentions } : {}), updatedAt: serverTimestamp() }) as any);
}

export async function archiveCommunityPost(postId: string): Promise<void> {
  await ensureAuthedForWrite('archiveCommunityPost');
  const resolvedPostId = String(postId || '').trim();
  if (!resolvedPostId) throw new Error('Missing community post id for archive');
  const uid = auth.currentUser?.uid || null;
  const ref = doc(db as any, 'communityPosts', resolvedPostId);
  await updateDoc(ref, stripUndefined({
    status: 'deleted',
    archived: true,
    deletedAt: serverTimestamp(),
    deletedBy: uid,
    updatedAt: serverTimestamp(),
  }) as any);
}

// Backward-compatible name used by App.tsx/CommunityView. This is now a soft delete
// so subcollections (comments/likes/replies) remain auditable and future-safe.
export async function deleteCommunityPost(postId: string): Promise<void> {
  return archiveCommunityPost(postId);
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

export async function addCommentToPost(postId: string, comment: Comment): Promise<string> {
  await ensureAuthedForWrite('addCommentToPost');

  const resolvedPostId = String(postId || '').trim();
  if (!resolvedPostId) throw new Error('Missing community post id for comment');

  const postRef = doc(db as any, 'communityPosts', resolvedPostId);
  const postSnap = await getDoc(postRef);
  if (!postSnap.exists()) throw new Error(`Community post not found: ${resolvedPostId}`);
  if (!isVisibleCommunityPost(postSnap.data())) throw new Error(`Community post is archived/deleted: ${resolvedPostId}`);

  const commentRef = doc(db as any, 'communityPosts', resolvedPostId, 'comments', comment.id);
  const mentions = await extractMentions(comment.text ?? '');

  const data: any = stripUndefined({
    id: comment.id,
    postId: resolvedPostId,
    text: comment.text,
    mentions,
    authorId: comment.authorId,
    authorName: comment.authorName,
    authorAvatar: comment.authorAvatar ?? null,
    authorUsername: (comment as any).authorUsername ?? (comment as any).username ?? null,
    timestamp: comment.timestamp ?? Date.now(),
    parentCommentId: comment.parentCommentId === undefined ? null : comment.parentCommentId,
    replyToAuthorId: comment.replyToAuthorId === undefined ? null : comment.replyToAuthorId,
    likeCount: Number((comment as any).likeCount ?? 0),
    replyCount: Number((comment as any).replyCount ?? 0),
    status: (comment as any).status,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  });

  const batch = writeBatch(db as any);
  batch.set(commentRef, data);
  batch.update(postRef, {
    commentCount: increment(1),
    lastCommentAt: comment.timestamp ?? Date.now(),
    updatedAt: serverTimestamp(),
  });

  if (comment.parentCommentId) {
    const parentRef = doc(db as any, 'communityPosts', resolvedPostId, 'comments', comment.parentCommentId);
    batch.update(parentRef, {
      replyCount: increment(1),
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();
  return comment.id;
}

export async function updateCommentOnPost(postId: string, commentId: string, newText: string): Promise<void> {
  await ensureAuthedForWrite('updateCommentOnPost');
  const ref = doc(db as any, 'communityPosts', postId, 'comments', commentId);
  const mentions = await extractMentions(newText);
  await updateDoc(ref, stripUndefined({ text: newText, mentions, updatedAt: serverTimestamp() }) as any);
}

export async function deleteCommentFromPost(postId: string, commentId: string): Promise<void> {
  await ensureAuthedForWrite('deleteCommentFromPost');

  const commentRef = doc(db as any, 'communityPosts', postId, 'comments', commentId);
  const snap = await getDoc(commentRef);
  if (!snap.exists()) return;

  const data = snap.data() as any;
  const parentCommentId = data?.parentCommentId ?? null;

  const batch = writeBatch(db as any);
  batch.delete(commentRef);

  batch.update(doc(db as any, 'communityPosts', postId), {
    commentCount: increment(-1),
    updatedAt: serverTimestamp(),
  });

  if (parentCommentId) {
    batch.update(doc(db as any, 'communityPosts', postId, 'comments', parentCommentId), {
      replyCount: increment(-1),
      updatedAt: serverTimestamp(),
    });
  }

  await batch.commit();
}

// ---------------------------------------------------------------------------
// Likes (subcollection-based)
// ---------------------------------------------------------------------------

export async function setPostReaction(
  postId: string,
  uid: string,
  reaction: CommunityReactionKey | null,
): Promise<void> {
  const postRef = doc(db as any, 'communityPosts', postId);
  const likeRef = doc(db as any, 'communityPosts', postId, 'likes', uid);

  await runTransaction(db as any, async (tx) => {
    const [postSnap, likeSnap] = await Promise.all([
      tx.get(postRef),
      tx.get(likeRef),
    ]);

    if (!postSnap.exists()) throw new Error('Post not found');
    if (!isVisibleCommunityPost(postSnap.data())) throw new Error('Post is archived/deleted');

    if (!reaction) {
      if (likeSnap.exists()) {
        tx.delete(likeRef);
        tx.update(postRef, {
          likeCount: increment(-1),
          updatedAt: serverTimestamp(),
        });
      }
      return;
    }

    const safeReaction = normalizeCommunityReaction(reaction);

    if (likeSnap.exists()) {
      tx.update(likeRef, {
        uid,
        reaction: safeReaction,
        updatedAt: serverTimestamp(),
      });
      tx.update(postRef, { updatedAt: serverTimestamp() });
    } else {
      tx.set(likeRef, {
        uid,
        reaction: safeReaction,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      tx.update(postRef, {
        likeCount: increment(1),
        updatedAt: serverTimestamp(),
      });
    }
  });
}

export async function togglePostLike(
  postId: string,
  uid: string,
  reaction: CommunityReactionKey = 'like',
): Promise<void> {
  const postRef = doc(db as any, 'communityPosts', postId);
  const likeRef = doc(db as any, 'communityPosts', postId, 'likes', uid);

  await runTransaction(db as any, async (tx) => {
    const [postSnap, likeSnap] = await Promise.all([
      tx.get(postRef),
      tx.get(likeRef),
    ]);

    if (!postSnap.exists()) throw new Error('Post not found');
    if (!isVisibleCommunityPost(postSnap.data())) throw new Error('Post is archived/deleted');

    const safeReaction = normalizeCommunityReaction(reaction);

    if (likeSnap.exists()) {
      const currentReaction = readLikeReaction(likeSnap.data());

      // Tap same reaction again = remove. Choosing a different emoji from the
      // tray updates the existing reaction without decrementing likeCount.
      if (currentReaction === safeReaction) {
        tx.delete(likeRef);
        tx.update(postRef, {
          likeCount: increment(-1),
          updatedAt: serverTimestamp(),
        });
      } else {
        tx.update(likeRef, {
          uid,
          reaction: safeReaction,
          updatedAt: serverTimestamp(),
        });
        tx.update(postRef, { updatedAt: serverTimestamp() });
      }
    } else {
      tx.set(likeRef, {
        uid,
        reaction: safeReaction,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      tx.update(postRef, {
        likeCount: increment(1),
        updatedAt: serverTimestamp(),
      });
    }
  });
}

export async function setCommentReaction(
  postId: string,
  commentId: string,
  uid: string,
  reaction: CommunityReactionKey | null,
): Promise<void> {
  const commentRef = doc(db as any, 'communityPosts', postId, 'comments', commentId);
  const likeRef = doc(db as any, 'communityPosts', postId, 'comments', commentId, 'likes', uid);

  await runTransaction(db as any, async (tx) => {
    const [commentSnap, likeSnap] = await Promise.all([
      tx.get(commentRef),
      tx.get(likeRef),
    ]);

    if (!commentSnap.exists()) throw new Error('Comment not found');

    if (!reaction) {
      if (likeSnap.exists()) {
        tx.delete(likeRef);
        tx.update(commentRef, {
          likeCount: increment(-1),
          updatedAt: serverTimestamp(),
        });
      }
      return;
    }

    const safeReaction = normalizeCommunityReaction(reaction);

    if (likeSnap.exists()) {
      tx.update(likeRef, {
        uid,
        reaction: safeReaction,
        updatedAt: serverTimestamp(),
      });
      tx.update(commentRef, { updatedAt: serverTimestamp() });
    } else {
      tx.set(likeRef, {
        uid,
        reaction: safeReaction,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      tx.update(commentRef, {
        likeCount: increment(1),
        updatedAt: serverTimestamp(),
      });
    }
  });
}

export async function toggleCommentLike(
  postId: string,
  commentId: string,
  uid: string,
  reaction: CommunityReactionKey = 'like',
): Promise<void> {
  const commentRef = doc(db as any, 'communityPosts', postId, 'comments', commentId);
  const likeRef = doc(db as any, 'communityPosts', postId, 'comments', commentId, 'likes', uid);

  await runTransaction(db as any, async (tx) => {
    const [commentSnap, likeSnap] = await Promise.all([
      tx.get(commentRef),
      tx.get(likeRef),
    ]);

    if (!commentSnap.exists()) throw new Error('Comment not found');

    const safeReaction = normalizeCommunityReaction(reaction);

    if (likeSnap.exists()) {
      const currentReaction = readLikeReaction(likeSnap.data());

      // Tap same reaction again = remove. Choosing a different emoji from the
      // tray updates the existing reaction without decrementing likeCount.
      if (currentReaction === safeReaction) {
        tx.delete(likeRef);
        tx.update(commentRef, {
          likeCount: increment(-1),
          updatedAt: serverTimestamp(),
        });
      } else {
        tx.update(likeRef, {
          uid,
          reaction: safeReaction,
          updatedAt: serverTimestamp(),
        });
        tx.update(commentRef, { updatedAt: serverTimestamp() });
      }
    } else {
      tx.set(likeRef, {
        uid,
        reaction: safeReaction,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
      tx.update(commentRef, {
        likeCount: increment(1),
        updatedAt: serverTimestamp(),
      });
    }
  });
}


export function subscribeMyPostLikes(
  postIds: string[],
  uid: string,
  onChange: (map: Record<string, boolean | CommunityReactionKey>) => void,
): () => void {
  const safePostIds = Array.from(new Set((postIds || []).filter(Boolean))).slice(0, 50);
  const unsubs: Array<() => void> = [];
  const state: Record<string, boolean | CommunityReactionKey> = {};

  const emit = () => onChange({ ...state });

  for (const postId of safePostIds) {
    const ref = doc(db as any, 'communityPosts', postId, 'likes', uid);
    const unsub = onSnapshot(
      ref,
      (snap) => {
        state[postId] = snap.exists() ? readLikeReaction(snap.data()) : false;
        emit();
      },
      () => {
        state[postId] = false;
        emit();
      },
    );
    unsubs.push(unsub);
  }

  return () => {
    unsubs.forEach((u) => {
      try { u(); } catch {}
    });
  };
}

export function subscribeMyCommentLikes(
  keys: Array<{ postId: string; commentId: string }>,
  uid: string,
  onChange: (map: Record<string, boolean | CommunityReactionKey>) => void,
): () => void {
  const safeKeys = Array.from(
    new Map(
      (keys || [])
        .filter((k) => k?.postId && k?.commentId)
        .map((k) => [`${k.postId}__${k.commentId}`, k]),
    ).values(),
  ).slice(0, 50);

  const unsubs: Array<() => void> = [];
  const state: Record<string, boolean | CommunityReactionKey> = {};

  const emit = () => onChange({ ...state });

  for (const { postId, commentId } of safeKeys) {
    const key = `${postId}__${commentId}`;
    const ref = doc(db as any, 'communityPosts', postId, 'comments', commentId, 'likes', uid);

    const unsub = onSnapshot(
      ref,
      (snap) => {
        state[key] = snap.exists() ? readLikeReaction(snap.data()) : false;
        emit();
      },
      () => {
        state[key] = false;
        emit();
      },
    );

    unsubs.push(unsub);
  }

  return () => {
    unsubs.forEach((u) => {
      try { u(); } catch {}
    });
  };
}

// ---------------------------------------------------------------------------
// Legacy group listener (if used elsewhere)
// ---------------------------------------------------------------------------

export function subscribeAllComments(
  onCommentsByPost: (commentsByPost: Record<string, Comment[]>) => void,
) {
  const qRef = query(
    collectionGroup(db as any, 'comments'),
    orderBy('postId'),
    orderBy('createdAt', 'asc'),
  );

  return onSnapshot(qRef, (snap) => {
    const byPost: Record<string, Comment[]> = {};

    snap.forEach((docSnap) => {
      const c = normalizeCommentFromSnap(docSnap);
      const postId = (c as any).postId;
      if (!postId) return;
      if (!byPost[postId]) byPost[postId] = [];
      byPost[postId].push(c);
    });

    onCommentsByPost(byPost);
  });
}